import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CardActions,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Grid,
  Snackbar,
  Stack,
  Typography,
} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowOutwardIcon from '@mui/icons-material/ArrowOutward';
import CallOutlinedIcon from '@mui/icons-material/CallOutlined';
import CallReceivedIcon from '@mui/icons-material/CallReceived';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import PhoneForwardedIcon from '@mui/icons-material/PhoneForwarded';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import RecordVoiceOverOutlinedIcon from '@mui/icons-material/RecordVoiceOverOutlined';
import RuleIcon from '@mui/icons-material/Rule';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import { useLocation, useMatch, useNavigate, useParams } from 'react-router-dom';
import * as amplitude from '@amplitude/analytics-browser';
import AgentConversationListView from '../components/AgentConversationListView';
import AgentSetupFlow from '../components/AgentSetupFlow';
import { useUserData } from '../hooks/useUserData';
import {
  createAgentForUser,
  deleteAgentForUser,
  fetchUserAgents,
  getUserAgentPhoneNumbers,
  updateAgentForUser,
} from '../services/agentService';
import { getAgentVoiceOption } from '../constants/agentVoices';
import { canAccessAIAgents } from '../planAccess';

const FORM_URL = 'https://tally.so/r/81dRdO';

const featureCards = [
  {
    icon: <CallReceivedIcon />,
    title: 'Պատասխանում են զանգերին',
    description: 'Ագենտները կարող են ընդունել մուտքային զանգեր, հասկանալ զանգահարողի նպատակը և գրանցել կարևոր տվյալները։',
  },
  {
    icon: <PhoneForwardedIcon />,
    title: 'Զանգահարում են ձեր փոխարեն',
    description: 'Ագենտները կարող են կատարել ելքային զանգեր՝ հետևելով ձեր ցուցումներին և նախապես սահմանված ընթացակարգերին։',
  },
  {
    icon: <RuleIcon />,
    title: 'Աշխատում են ձեր կանոններով',
    description: 'Յուրաքանչյուր գործողություն կառավարվում է ձեր ցուցումներով և տվյալներով, որպեսզի զանգերի ընթացքը համընկնի ձեր գործընթացներին։',
  },
];

const buildAgentPreview = (agent) => ({
  id: agent?.id || '',
  name: agent?.name || '',
  phoneNumberSnapshot: agent?.phoneNumberSnapshot || null,
});

const PageLoadingState = () => (
  <Container maxWidth="lg" sx={{ py: 3 }}>
    <Box sx={{ minHeight: 420, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Stack spacing={2} alignItems="center">
        <CircularProgress size={42} />
        <Typography color="text.secondary">Բեռնվում է…</Typography>
      </Stack>
    </Box>
  </Container>
);

const AgentsComingSoonView = ({ user }) => {
  const notifyMeUrl = useMemo(() => {
    const url = new URL(FORM_URL);
    if (user?.email) {
      url.searchParams.set('prefilled_email', user.email);
    }
    return url.toString();
  }, [user?.email]);

  const handleNotifyMeClick = () => {
    amplitude.track('Click on AI agents notify me', {
      userId: user?.uid,
      hasPrefilledEmail: Boolean(user?.email),
    });
  };

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 2, sm: 3 } }}>
      <Box
        sx={{
          position: 'relative',
          overflow: 'hidden',
          borderRadius: 4,
          p: { xs: 3, sm: 4, md: 5 },
          mb: 3,
          color: '#ffffff',
          background: 'linear-gradient(135deg, #0f172a 0%, #1d4ed8 60%, #0ea5e9 100%)',
          boxShadow: '0 18px 40px rgba(2, 8, 23, 0.35)',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: -80,
            right: -50,
            width: 220,
            height: 220,
            borderRadius: '50%',
            backgroundColor: 'rgba(255, 255, 255, 0.14)',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            bottom: -70,
            left: -30,
            width: 180,
            height: 180,
            borderRadius: '50%',
            backgroundColor: 'rgba(255, 255, 255, 0.10)',
          }}
        />

        <Stack spacing={2} sx={{ position: 'relative' }}>
          <Chip
            icon={<SmartToyIcon sx={{ color: '#0f172a !important' }} />}
            label="Շուտով"
            sx={{
              alignSelf: 'flex-start',
              fontWeight: 700,
              backgroundColor: '#e2e8f0',
              color: '#0f172a',
            }}
          />
          <Typography variant="h3" sx={{ fontWeight: 800, fontSize: { xs: '1.9rem', md: '2.4rem' }, lineHeight: 1.2 }}>
            AI Ագենտներ
          </Typography>
          <Typography variant="h6" sx={{ maxWidth: 760, color: 'rgba(255, 255, 255, 0.9)', fontWeight: 400 }}>
            AI ագենտներ, որոնք ձեր փոխարեն ընդունում և կատարում են զանգեր` հիմնվելով ձեր ցուցումների և տվյալների վրա։
          </Typography>
          <Box sx={{ pt: 1 }}>
            <Button
              component="a"
              href={notifyMeUrl}
              target="_blank"
              rel="noopener noreferrer"
              variant="contained"
              endIcon={<ArrowOutwardIcon />}
              onClick={handleNotifyMeClick}
              sx={{
                fontWeight: 700,
                px: 3,
                py: 1.2,
                borderRadius: 2,
                backgroundColor: '#ffffff',
                color: '#0f172a',
                '&:hover': {
                  backgroundColor: '#dbeafe',
                },
              }}
            >
              Տեղեկացրեք ինձ երբ պատրաստ լինեն
            </Button>
          </Box>
        </Stack>
      </Box>

      <Grid container spacing={2.5}>
        {featureCards.map((item) => (
          <Grid key={item.title} item xs={12} md={4}>
            <Card
              sx={{
                height: '100%',
                borderRadius: 3,
                border: '1px solid #e2e8f0',
                boxShadow: '0 8px 22px rgba(15, 23, 42, 0.06)',
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Box
                  sx={{
                    width: 46,
                    height: 46,
                    borderRadius: 2,
                    mb: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: '#e0f2fe',
                    color: '#075985',
                  }}
                >
                  {item.icon}
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                  {item.title}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  {item.description}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Container>
  );
};

const AgentCatalogView = ({
  agents,
  onCreateAgent,
  onOpenAgent,
}) => (
  <>
    <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2} alignItems={{ xs: 'flex-start', md: 'center' }} sx={{ mb: 4 }}>
      <Box>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 700, mb: 1 }}>
          AI Ագենտներ
        </Typography>
        <Typography color="text.secondary" sx={{ maxWidth: 760, lineHeight: 1.7 }}>
          Ստեղծեք և կառավարեք ձեր զանգերին պատասխանող ագենտները, կապեք համարները և հետևեք նրանց բոլոր խոսակցություններին մեկ տեղում։
        </Typography>
      </Box>
      <Button
        variant="contained"
        size="large"
        startIcon={<AddCircleOutlineIcon />}
        onClick={onCreateAgent}
        sx={{
          py: 1.5,
          px: 3,
          borderRadius: 2,
          textTransform: 'none',
          fontWeight: 700,
        }}
      >
        Ստեղծել նոր ագենտ
      </Button>
    </Stack>

    <Grid container spacing={3}>
      <Grid item xs={12} sm={6} md={4}>
        <Card
          elevation={0}
          sx={{
            height: '100%',
            borderRadius: 4,
            borderStyle: 'dashed',
            borderWidth: 1,
            borderColor: '#cbd5e1',
            backgroundColor: '#ffffff',
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              boxShadow: 6,
              transform: 'translateY(-2px)',
            },
          }}
        >
          <CardActionArea
            onClick={onCreateAgent}
            sx={{
              height: '100%',
              display: 'flex',
              alignItems: 'stretch',
            }}
          >
            <CardContent
              sx={{
                p: 3,
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                minHeight: 280,
              }}
            >
              <Box>
                <Box
                  sx={{
                    width: 54,
                    height: 54,
                    borderRadius: 3,
                    mb: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: '#f1f5f9',
                    color: '#334155',
                  }}
                >
                  <AddCircleOutlineIcon sx={{ fontSize: 30 }} />
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                  Ստեղծել նոր ագենտ
                </Typography>
                <Typography color="text.secondary" sx={{ lineHeight: 1.7 }}>
                  Բացեք ամբողջական կոնֆիգուրացիայի էկրանը, լրացրեք ընդհանուր տվյալները, վարքագիծը և գիտելիքների բազան, ապա կապեք համարը։
                </Typography>
              </Box>
            </CardContent>
          </CardActionArea>
        </Card>
      </Grid>

      {agents.map((agent) => {
        const voiceOption = getAgentVoiceOption(agent.voice);

        return (
          <Grid item xs={12} sm={6} md={4} key={agent.id}>
            <Card
              elevation={0}
              sx={{
                height: '100%',
                borderRadius: 4,
                border: '1px solid #e2e8f0',
                boxShadow: '0 4px 18px rgba(15, 23, 42, 0.05)',
                display: 'flex',
                flexDirection: 'column',
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  boxShadow: 6,
                  transform: 'translateY(-2px)',
                },
              }}
            >
              <CardActionArea onClick={() => onOpenAgent(agent)} sx={{ flex: 1, alignItems: 'stretch' }}>
                <CardContent sx={{ p: 3, flex: 1 }}>
                  <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ mb: 2 }}>
                    <Box
                      sx={{
                        width: 46,
                        height: 46,
                        borderRadius: 2.5,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: '#f1f5f9',
                        color: '#334155',
                      }}
                    >
                      <SmartToyOutlinedIcon />
                    </Box>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }} noWrap>
                        {agent.name}
                      </Typography>
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                        <PhoneOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                        <Typography variant="body2" color="text.secondary" noWrap>
                          {agent.phoneNumberSnapshot?.phoneNumber || 'Համար չի ընտրվել'}
                        </Typography>
                      </Stack>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <RecordVoiceOverOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                        <Typography variant="body2" color="text.secondary" noWrap>
                          {voiceOption.title}
                        </Typography>
                      </Stack>
                    </Box>
                  </Stack>

                </CardContent>
              </CardActionArea>

              <CardActions sx={{ px: 3, pb: 3, pt: 0 }}>
                <Button
                  variant="outlined"
                  startIcon={<CallOutlinedIcon />}
                  onClick={() => onOpenAgent(agent)}
                  sx={{ flex: 1, textTransform: 'none', fontWeight: 700 }}
                >
                  Տեսնել զանգերը
                </Button>
              </CardActions>
            </Card>
          </Grid>
        );
      })}
    </Grid>
  </>
);

const AgentEditorLoadingView = ({ onBack }) => (
  <Stack spacing={2.5}>
    <Button
      variant="outlined"
      startIcon={<ArrowBackIcon />}
      onClick={onBack}
      sx={{ alignSelf: 'flex-start', textTransform: 'none', fontWeight: 700 }}
    >
      Վերադառնալ ագենտին
    </Button>
    <Box
      sx={{
        minHeight: 320,
        borderRadius: 4,
        border: '1px solid #e2e8f0',
        bgcolor: '#ffffff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Stack spacing={2} alignItems="center">
        <CircularProgress size={40} />
        <Typography color="text.secondary">Բեռնվում է…</Typography>
      </Stack>
    </Box>
  </Stack>
);

const AgentCreatedSuccessView = ({ agent, onOpenConversations, onEditAgent }) => {
  const confettiPieces = Array.from({ length: 18 }, (_, index) => ({
    id: index,
    left: `${5 + (index * 5)}%`,
    delay: `${(index % 6) * 0.22}s`,
    duration: `${3.1 + ((index % 5) * 0.3)}s`,
    rotate: `${(index % 2 === 0 ? 1 : -1) * (12 + index * 4)}deg`,
    color: ['#38bdf8', '#34d399', '#fbbf24', '#fb7185', '#a78bfa'][index % 5],
  }));

  return (
    <Card
      elevation={0}
      sx={{
        position: 'relative',
        overflow: 'hidden',
        borderRadius: 5,
        border: '1px solid #e2e8f0',
        backgroundColor: '#ffffff',
        boxShadow: '0 16px 36px rgba(15, 23, 42, 0.06)',
        '@keyframes confettiDrop': {
          '0%': {
            opacity: 0,
            transform: 'translateY(-20px) rotate(0deg)',
          },
          '10%': {
            opacity: 1,
          },
          '100%': {
            opacity: 0,
            transform: 'translateY(340px) rotate(360deg)',
          },
        },
      }}
    >
      {confettiPieces.map((piece) => (
        <Box
          key={piece.id}
          sx={{
            position: 'absolute',
            top: -14,
            left: piece.left,
            width: 10,
            height: 18,
            borderRadius: 999,
            backgroundColor: piece.color,
            transform: `rotate(${piece.rotate})`,
            animation: `confettiDrop ${piece.duration} linear ${piece.delay} infinite`,
          }}
        />
      ))}

      <CardContent sx={{ p: { xs: 3, md: 4.5 }, position: 'relative', zIndex: 1 }}>
        <Stack spacing={3}>
          <Stack spacing={1.5} alignItems="center" textAlign="center" sx={{ pt: { xs: 2, md: 3 } }}>
            <Box
              sx={{
                width: 88,
                height: 88,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: '#f0fdf4',
                color: '#15803d',
                border: '1px solid #bbf7d0',
              }}
            >
              <TaskAltIcon sx={{ fontSize: 44 }} />
            </Box>
            <Typography variant="overline" sx={{ letterSpacing: '0.12em', color: '#475569', fontWeight: 800 }}>
              ՊԱՏՐԱՍՏ Է
            </Typography>
            <Typography variant="h3" sx={{ fontWeight: 900, lineHeight: 1.05 }}>
              «{agent?.name || 'AI ագենտ'}» ագենտը ստեղծված է
            </Typography>
            <Typography color="text.secondary" sx={{ maxWidth: 720, lineHeight: 1.8 }}>
              Կարող եք անմիջապես զանգահարել այս համարին և փորձարկել ագենտին իրական զանգով։ Երբ խոսակցությունն ավարտվի, ձայնագրություններն ու զրույցների պատմությունը կտեսնեք ագենտի էջում։
            </Typography>
          </Stack>

          <Grid container spacing={2.5}>
            <Grid item xs={12} md={6}>
              <Card
                elevation={0}
                sx={{
                  height: '100%',
                  borderRadius: 4,
                  border: '1px solid #e2e8f0',
                  backgroundColor: '#f8fafc',
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 1.5 }}>
                    <PhoneOutlinedIcon sx={{ color: '#475569' }} />
                    <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                      Փորձարկման համարը
                    </Typography>
                  </Stack>
                  <Typography variant="h4" sx={{ fontWeight: 900, mb: 1 }}>
                    {agent?.phoneNumberSnapshot?.phoneNumber || 'Համար չի ընտրվել'}
                  </Typography>
                  <Typography color="text.secondary" sx={{ lineHeight: 1.7 }}>
                    Զանգահարեք այս համարին, որպեսզի լսեք ողջույնի խոսքը, ստուգեք վարքագիծը և համոզվեք, որ ագենտը ճիշտ է արձագանքում։
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card
                elevation={0}
                sx={{
                  height: '100%',
                  borderRadius: 4,
                  border: '1px solid #e2e8f0',
                  backgroundColor: '#f8fafc',
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 1.5 }}>
                    <CallOutlinedIcon sx={{ color: '#475569' }} />
                    <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                      Ինչ կլինի հետո
                    </Typography>
                  </Stack>
                  <Typography color="text.secondary" sx={{ lineHeight: 1.7 }}>
                    Ավարտված զանգերից հետո ագենտի էջում կտեսնեք ձայնագրությունները, խոսակցության տևողությունը և բոլոր հետագա զրույցների պատմությունը։
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} justifyContent="center">
            <Button
              variant="contained"
              size="large"
              startIcon={<CallOutlinedIcon />}
              onClick={onOpenConversations}
              sx={{ minWidth: 240, textTransform: 'none', fontWeight: 800 }}
            >
              Բացել ագենտի էջը
            </Button>
            <Button
              variant="outlined"
              size="large"
              startIcon={<EditOutlinedIcon />}
              onClick={onEditAgent}
              sx={{ minWidth: 240, textTransform: 'none', fontWeight: 800 }}
            >
              Խմբագրել կարգավորումը
            </Button>
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
};

const AIAgents = ({ user }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { agentId: routeAgentId = '' } = useParams();
  const isCreateRoute = Boolean(useMatch('/ai-agents/new'));
  const isEditRoute = Boolean(useMatch('/ai-agents/:agentId/edit'));
  const isDetailRoute = Boolean(useMatch('/ai-agents/:agentId'));
  const [agents, setAgents] = useState([]);
  const [loadingAgents, setLoadingAgents] = useState(true);
  const [agentsLoaded, setAgentsLoaded] = useState(false);
  const [agentLoadFailed, setAgentLoadFailed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const { userData, loadingUserData } = useUserData();
  const hasAIAgentsAccess = canAccessAIAgents(userData?.plan);

  const routeAgentPreview = useMemo(() => {
    const preview = location.state?.agentPreview;

    if (!routeAgentId || !preview || preview.id !== routeAgentId) {
      return null;
    }

    return preview;
  }, [location.state, routeAgentId]);

  const hydratedSelectedAgent = useMemo(
    () => agents.find((agent) => agent.id === routeAgentId) || null,
    [agents, routeAgentId]
  );

  const selectedAgent = useMemo(() => {
    if (!routeAgentId) {
      return null;
    }

    return hydratedSelectedAgent || routeAgentPreview || { id: routeAgentId };
  }, [hydratedSelectedAgent, routeAgentId, routeAgentPreview]);

  const phoneNumbersLoading = loadingUserData || loadingAgents;
  const phoneNumbers = useMemo(
    () => getUserAgentPhoneNumbers({ userData, agents }),
    [agents, userData]
  );
  const showCreatedSuccess = isDetailRoute && Boolean(location.state?.justCreated);
  const shouldBlockForAgentData = hasAIAgentsAccess
    && (loadingAgents || !agentsLoaded)
    && (isCreateRoute || isEditRoute || !routeAgentId || !routeAgentPreview);

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const loadAgents = async () => {
    if (!user?.uid) {
      return [];
    }

    setLoadingAgents(true);
    setAgentsLoaded(false);
    setAgentLoadFailed(false);

    try {
      const nextAgents = await fetchUserAgents(user.uid);
      setAgents(nextAgents);
      setAgentsLoaded(true);
      return nextAgents;
    } catch (error) {
      console.error('Error loading AI agents:', error);
      setAgentLoadFailed(true);
      setAgentsLoaded(true);
      showSnackbar('Ագենտների տվյալները բեռնելիս առաջացավ սխալ։', 'error');
      return [];
    } finally {
      setLoadingAgents(false);
    }
  };

  useEffect(() => {
    let isMounted = true;

    if (loadingUserData) {
      return undefined;
    }

    if (!hasAIAgentsAccess || !user?.uid) {
      setAgents([]);
      setLoadingAgents(false);
      setAgentsLoaded(false);
      setAgentLoadFailed(false);
      return undefined;
    }

    (async () => {
      try {
        if (isMounted) {
          setLoadingAgents(true);
          setAgentsLoaded(false);
          setAgentLoadFailed(false);
        }

        const nextAgents = await fetchUserAgents(user.uid);

        if (isMounted) {
          setAgents(nextAgents);
          setAgentsLoaded(true);
        }
      } catch (error) {
        console.error('Error loading AI agents:', error);
        if (isMounted) {
          setAgentLoadFailed(true);
          setAgentsLoaded(true);
          showSnackbar('Ագենտների տվյալները բեռնելիս առաջացավ սխալ։', 'error');
        }
      } finally {
        if (isMounted) {
          setLoadingAgents(false);
        }
      }
    })();

    return () => {
      isMounted = false;
    };
  }, [hasAIAgentsAccess, loadingUserData, user?.uid]);

  useEffect(() => {
    if (!routeAgentId || !agentsLoaded || loadingAgents || agentLoadFailed) {
      return;
    }

    if (!hydratedSelectedAgent) {
      showSnackbar('Ագենտը չի գտնվել։', 'error');
      navigate('/ai-agents', { replace: true });
    }
  }, [agentLoadFailed, agentsLoaded, hydratedSelectedAgent, loadingAgents, navigate, routeAgentId]);

  const navigateToAgentDetail = (agent, extraState = {}) => {
    navigate(`/ai-agents/${agent.id}`, {
      replace: Boolean(extraState.replace),
      state: {
        agentPreview: buildAgentPreview(agent),
        ...extraState.state,
      },
    });
  };

  const handleSubmitCreateAgent = async (formData) => {
    if (!user?.uid) {
      return;
    }

    setSubmitting(true);

    try {
      const createdAgent = await createAgentForUser({
        userId: user.uid,
        userData,
        data: formData,
        agents,
      });

      const nextAgents = await loadAgents();
      const resolvedAgent = nextAgents.find((agent) => agent.id === createdAgent.id) || createdAgent;

      navigate(`/ai-agents/${resolvedAgent.id}`, {
        state: {
          agentPreview: buildAgentPreview(resolvedAgent),
          justCreated: true,
        },
      });
    } catch (error) {
      console.error('Error creating agent:', error);
      showSnackbar(error.message || 'Ագենտը ստեղծել չհաջողվեց։', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitEditAgent = async (formData) => {
    if (!user?.uid || !hydratedSelectedAgent?.id) {
      return;
    }

    setSubmitting(true);

    try {
      await updateAgentForUser({
        userId: user.uid,
        userData,
        agentId: hydratedSelectedAgent.id,
        data: formData,
        agents,
      });

      const nextAgents = await loadAgents();
      const resolvedAgent = nextAgents.find((agent) => agent.id === hydratedSelectedAgent.id) || hydratedSelectedAgent;

      navigate(`/ai-agents/${resolvedAgent.id}`, {
        replace: true,
        state: { agentPreview: buildAgentPreview(resolvedAgent) },
      });
      showSnackbar('Ագենտի կարգավորումը պահպանվեց։');
    } catch (error) {
      console.error('Error saving agent:', error);
      showSnackbar(error.message || 'Ագենտը պահպանել չհաջողվեց։', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteAgentFromEditor = async (agentRecord) => {
    if (!agentRecord?.id) {
      return;
    }

    setSubmitting(true);

    try {
      await deleteAgentForUser({ agentId: agentRecord.id });
      await loadAgents();
      navigate('/ai-agents', { replace: true });
      showSnackbar('Ագենտը ջնջվեց։');
    } catch (error) {
      console.error('Error deleting agent from editor:', error);
      showSnackbar(error.message || 'Ագենտը ջնջել չհաջողվեց։', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const clearCreatedState = () => {
    if (!selectedAgent?.id) {
      return;
    }

    navigate(`/ai-agents/${selectedAgent.id}`, {
      replace: true,
      state: { agentPreview: buildAgentPreview(selectedAgent) },
    });
  };

  if (loadingUserData || shouldBlockForAgentData) {
    return <PageLoadingState />;
  }

  if (!hasAIAgentsAccess) {
    return <AgentsComingSoonView user={user} />;
  }

  return (
    <Container maxWidth="xl" sx={{ py: { xs: 2, md: 3 } }}>
      {isCreateRoute ? (
        <AgentSetupFlow
          mode="create"
          phoneNumbers={phoneNumbers}
          phoneNumbersLoading={phoneNumbersLoading}
          submitting={submitting}
          onCancel={() => navigate('/ai-agents')}
          onSubmit={handleSubmitCreateAgent}
        />
      ) : isEditRoute ? (
        hydratedSelectedAgent && !phoneNumbersLoading ? (
          <AgentSetupFlow
            mode="edit"
            agent={hydratedSelectedAgent}
            phoneNumbers={phoneNumbers}
            phoneNumbersLoading={phoneNumbersLoading}
            submitting={submitting}
            onCancel={() => navigate(`/ai-agents/${hydratedSelectedAgent.id}`, {
              state: { agentPreview: buildAgentPreview(hydratedSelectedAgent) },
            })}
            onDelete={handleDeleteAgentFromEditor}
            onSubmit={handleSubmitEditAgent}
          />
        ) : (
          <AgentEditorLoadingView
            onBack={() => navigate(routeAgentId ? `/ai-agents/${routeAgentId}` : '/ai-agents')}
          />
        )
      ) : routeAgentId && selectedAgent ? (
        showCreatedSuccess ? (
          <AgentCreatedSuccessView
            agent={hydratedSelectedAgent || selectedAgent}
            onOpenConversations={clearCreatedState}
            onEditAgent={() => navigate(`/ai-agents/${selectedAgent.id}/edit`, {
              state: { agentPreview: buildAgentPreview(hydratedSelectedAgent || selectedAgent) },
            })}
          />
        ) : (
          <AgentConversationListView
            agent={selectedAgent}
            onBack={() => navigate('/ai-agents')}
            onEditAgent={() => navigate(`/ai-agents/${selectedAgent.id}/edit`, {
              state: { agentPreview: buildAgentPreview(hydratedSelectedAgent || selectedAgent) },
            })}
            onOpenConversation={(conversationId) => navigate(`/ai-agents/${selectedAgent.id}/conversations/${encodeURIComponent(conversationId)}`)}
          />
        )
      ) : (
        <AgentCatalogView
          agents={agents}
          onCreateAgent={() => navigate('/ai-agents/new')}
          onOpenAgent={(agent) => navigateToAgentDetail(agent)}
        />
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((previous) => ({ ...previous, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar((previous) => ({ ...previous, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default AIAgents;
